import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const FALLBACK_MESSAGE =
     'We are temporarily unavailable. Please check back soon!';

// Reads the store_offline row from admin_settings.
// Returns { offline, message, loading } — when offline is true, callers should
// show the message and disable booking CTAs.
//
// Callers MUST respect `loading`: offline is false until the row arrives, so
// treating a still-loading status as "open" leaves the CTAs live during the
// fetch.
//
// Fails CLOSED. If the row can't be read — network error, RLS, missing row — we
// report the store as offline rather than assuming it's open. Booking needs the
// database anyway, so a read we can't trust costs no real bookings, while
// guessing "open" lets customers book a closed store.
export default function useStoreStatus() {
     const [status, setStatus] = useState({
          offline: false,
          message: FALLBACK_MESSAGE,
          loading: true,
     });

     useEffect(() => {
          let active = true;
          (async () => {
               const { data, error } = await supabase
                    .from('admin_settings')
                    .select('value')
                    .eq('key', 'store_offline')
                    .maybeSingle();
               if (!active) return;

               if (error || !data?.value) {
                    // An empty result here usually means the anon role can't see
                    // the row (see migration 019) — never treat it as "open".
                    console.error(
                         '[useStoreStatus] could not read store_offline; treating the store as offline',
                         error
                    );
                    setStatus({
                         offline: true,
                         message: FALLBACK_MESSAGE,
                         loading: false,
                    });
                    return;
               }

               setStatus({
                    offline: !!data.value.enabled,
                    message: data.value.message || FALLBACK_MESSAGE,
                    loading: false,
               });
          })();
          return () => {
               active = false;
          };
     }, []);

     return status;
}
